/**
 * AK-460 — даты NDVI-подложки для карточки поля.
 *
 * Баг (founder staging-UAT, поле 000 «Каменное»): в «Геометрии» полоска дат
 * писала «Нет снимков за год», хотя во вкладке «Обзор» у ТОГО ЖЕ поля даты
 * были (25.07 / 22.07 / 20.07). Причина — вкладки читали РАЗНЫЕ источники с
 * разными инвариантами:
 *
 * - «Геометрия» (AK-442) звала `/api/v1/satellite/dates?scope_type=farm&…`.
 *   Этот роут (ADR-026 Phase C) требует, помимо самих растров, ещё и строку в
 *   `data.scene_complete` (её пишет пайплайн в момент коммита сцены) И запись
 *   в `data.field_indices_v2` по полям фермы. Нет бэкфилла этих таблиц для
 *   исторических сцен → пустой список, хотя снимки есть.
 * - «Обзор» зовёт `/api/v1/ndvi/perfield-png/{id}/latest`, который считает
 *   3-of-3 полноту сцены ЖИВЬЁМ по `data.scene_rasters` + `ST_Intersects` с
 *   контуром поля — без зависимости от тех двух таблиц. Поэтому и работает.
 *
 * Чиним на стороне карточки: берём даты, привязанные К ПОЛЮ, а не к ферме.
 * Это ещё и семантически правильнее — в карточке поля нужны даты, реально
 * накрывающие ЭТОТ контур, а не bbox всей фермы, и цифры сходятся с соседней
 * вкладкой (founder не видит противоречия между «Обзором» и «Геометрией»).
 *
 * Источники (по порядку):
 *  1. `/api/v1/ndvi/available-dates?field_id=…` — полная история по полю
 *     (`scene_rasters` ∩ геометрия поля). `min_field_coverage=0` +
 *     `min_clear_pixels=0` ОБЯЗАТЕЛЬНЫ: иначе включается пост-фильтр по
 *     `data.field_scene_coverage` — ещё одна таблица, которой может не быть,
 *     и мы снова получим пустой список.
 *  2. Фолбэк `/api/v1/ndvi/perfield-png/{id}/latest?n=12` — ровно тот
 *     источник, что уже работает в «Обзоре» (гарантированно scene-complete).
 *
 * Тайлы подложки при этом не рискуют остаться пустыми: роут
 * `/ndvi/raster/ndvi-contrast/{date}/…` при отсутствии precomputed
 * NDVI_CONTRAST COG сам падает в per-field render (см. его докстринг).
 */

/** Сколько последних снимков просим у фолбэк-роута (его кап — 12). */
const FALLBACK_LIMIT = 12;

interface AvailableDatesResponse {
  dates?: string[];
}

interface PerfieldLatestResponse {
  dates?: Array<{ date?: string }>;
}

function normalize(dates: Array<string | undefined | null>): string[] {
  return Array.from(
    new Set(dates.filter((d): d is string => typeof d === 'string' && d.length >= 10)),
  ).sort();
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T | null> {
  try {
    const r = await fetch(url, { credentials: 'include', signal });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    // abort / сеть — вызывающий покажет «нет снимков», не роняем карточку.
    return null;
  }
}

/**
 * Даты NDVI-снимков, реально накрывающих контур поля (ASC, уникальные).
 * Пустой массив = снимков действительно нет.
 */
export async function loadFieldNdviDates(
  fieldId: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const primary = await getJson<AvailableDatesResponse>(
    `/api/v1/ndvi/available-dates?field_id=${encodeURIComponent(fieldId)}` +
      `&index_code=ndvi&min_field_coverage=0&min_clear_pixels=0`,
    signal,
  );
  const dates = normalize(primary?.dates ?? []);
  if (dates.length) return dates;

  const fallback = await getJson<PerfieldLatestResponse>(
    `/api/v1/ndvi/perfield-png/${encodeURIComponent(fieldId)}/latest?n=${FALLBACK_LIMIT}`,
    signal,
  );
  return normalize((fallback?.dates ?? []).map((d) => d?.date));
}
